import { defineMessages, IntlShape, MessageDescriptor } from 'react-intl';
import { ASSET_SPECS, AssetSpecId } from 'fym-shared/src/music24/assetSpecs';
import { thumbnailVideoOfVariant, type KitShape, type ThumbnailVideo } from 'fym-shared/src/music24/studioThemes';

/**
 * One name per kit variant (`STUDIO_THEMES` outputs, `STORY_MOMENT_VARIANTS`,
 * `FEED_MOMENT_VARIANTS`), so the UI never prints a raw id such as "youtube" or "tall". A theme's
 * variant is a plain string; a variant with no name here prints the pixel size of its spec.
 */
const kitVariantMessages: Record<string, MessageDescriptor> = defineMessages({
  story: { id: 'app.sml.promote.kit.variant.story', defaultMessage: 'Story' },
  preSave: { id: 'app.sml.promote.kit.variant.preSave', defaultMessage: 'Pre-save' },
  outNow: { id: 'app.sml.promote.kit.variant.outNow', defaultMessage: 'Out now' },
  lyric: { id: 'app.sml.promote.kit.variant.lyric', defaultMessage: 'Lyric' },
  // `feed` is the feed post variant of rows made before each moment had its own variant.
  feed: { id: 'app.sml.promote.kit.variant.feed', defaultMessage: 'Feed post' },
  announce: { id: 'app.sml.promote.kit.variant.announce', defaultMessage: 'Announce' },
  youtube: { id: 'app.sml.promote.kit.variant.youtube', defaultMessage: 'YouTube' },
  x: { id: 'app.sml.promote.kit.variant.x', defaultMessage: 'X' },
  facebook: { id: 'app.sml.promote.kit.variant.facebook', defaultMessage: 'Facebook' },
  spotify: { id: 'app.sml.promote.kit.variant.spotify', defaultMessage: 'Spotify' },
  '3000': { id: 'app.sml.promote.kit.variant.cover3000', defaultMessage: '3000 × 3000' },
  '1400': { id: 'app.sml.promote.kit.variant.cover1400', defaultMessage: '1400 × 1400' },
  '640': { id: 'app.sml.promote.kit.variant.cover640', defaultMessage: '640 × 640' },
  square: { id: 'app.sml.promote.kit.variant.square', defaultMessage: 'Square' },
  tall: { id: 'app.sml.promote.kit.variant.tall', defaultMessage: 'Tall' },
  og: { id: 'app.sml.promote.kit.variant.og', defaultMessage: 'Link preview' },
  video: { id: 'app.sml.promote.kit.variant.video', defaultMessage: 'Video' },
  canvas: { id: 'app.sml.promote.kit.variant.canvas', defaultMessage: 'Canvas loop' },
  d7: { id: 'app.sml.promote.kit.variant.d7', defaultMessage: '7 days before' },
  d3: { id: 'app.sml.promote.kit.variant.d3', defaultMessage: '3 days before' },
  d1: { id: 'app.sml.promote.kit.variant.d1', defaultMessage: '1 day before' },
});

/** One name per shape (design n3): the word and the ratio. */
export const kitShapeMessages: Record<KitShape, MessageDescriptor> = defineMessages({
  '9:16': { id: 'app.sml.promote.kit.shape.tall', defaultMessage: 'Tall (9:16)' },
  '1:1': { id: 'app.sml.promote.kit.shape.square', defaultMessage: 'Square (1:1)' },
  '4:5': { id: 'app.sml.promote.kit.shape.portrait', defaultMessage: 'Portrait (4:5)' },
  '16:9': { id: 'app.sml.promote.kit.shape.wide', defaultMessage: 'Wide (16:9)' },
  '3:1': { id: 'app.sml.promote.kit.shape.banner', defaultMessage: 'Banner (3:1)' },
  '1.91:1': { id: 'app.sml.promote.kit.shape.link', defaultMessage: 'Link preview (1.91:1)' },
});

/** The name of a kit variant, for "Download · Tall". */
/** The name of a known variant ("Out now", "7 days to go"), or null for a size variant. */
export function kitVariantName(intl: IntlShape, variant: string): string | null {
  return Object.prototype.hasOwnProperty.call(kitVariantMessages, variant)
    ? intl.formatMessage(kitVariantMessages[variant] as MessageDescriptor)
    : null;
}

export function kitVariantLabel(intl: IntlShape, asset: { variant: string; specId: AssetSpecId }): string {
  const message = Object.prototype.hasOwnProperty.call(kitVariantMessages, asset.variant)
    ? kitVariantMessages[asset.variant]
    : undefined;
  if (message !== undefined) {
    return intl.formatMessage(message);
  }
  const spec = ASSET_SPECS[asset.specId];
  return `${spec.width} × ${spec.height}`;
}

/** The video of each YouTube thumbnail (fym-shared `THUMBNAIL_VIDEO_VARIANTS`). */
const thumbnailVideoMessages: Record<ThumbnailVideo, MessageDescriptor> = defineMessages({
  audio: { id: 'app.sml.promote.kit.thumbnail.audio', defaultMessage: 'Official audio' },
  lyricVideo: { id: 'app.sml.promote.kit.thumbnail.lyricVideo', defaultMessage: 'Lyric video' },
  visualizer: { id: 'app.sml.promote.kit.thumbnail.visualizer', defaultMessage: 'Visualizer' },
  musicVideo: { id: 'app.sml.promote.kit.thumbnail.musicVideo', defaultMessage: 'Music video' },
});

/**
 * The name of one output of an asset type, for a list of the files of a slot: a thumbnail names
 * its video ("Lyric video"), every other type its variant ("Square", "X").
 */
export function kitOutputLabel(
  intl: IntlShape,
  asset: { themeId: string; variant: string; specId: AssetSpecId }
): string {
  if (asset.themeId === 'thumbnail') {
    return intl.formatMessage(thumbnailVideoMessages[thumbnailVideoOfVariant(asset.variant)]);
  }
  return kitVariantLabel(intl, asset);
}
